import React, { useMemo, useState } from 'react';
import { Download, Plus } from 'lucide-react';
import {
  createTreasuryAccount,
  deleteTreasuryAccount,
  updateTreasuryAccount,
} from '../../../../services/treasuryAPI';
import TreasuryTable3 from './TreasuryTable3';
import { Pill, Stacked, TreasuryDrawer, buildFormStyles, downloadCsv, formatDate, money } from './treasuryUi';

const BANK_OPTIONS = [
  'Banco de Chile',
  'Banco Edwards',
  'BancoEstado',
  'Banco Santander',
  'Banco BCI',
  'Banco Itaú',
  'Scotiabank',
  'Banco Security',
  'Banco BICE',
  'Banco Falabella',
  'Banco Ripley',
  'Banco Internacional',
  'Banco Consorcio',
  'HSBC',
  'Otro',
];
const ACCOUNT_TYPE_OPTIONS = ['Cuenta corriente', 'Cuenta vista', 'Cuenta de ahorro', 'Línea de crédito', 'Otra'];
const CURRENCY_OPTIONS = ['CLP', 'USD', 'EUR'];

const emptyForm = () => ({
  account_name: '',
  bank_name: 'Banco de Chile',
  bank_account_type: 'Cuenta corriente',
  account_number: '',
  currency: 'CLP',
  holder_name: '',
  holder_rut: '',
  initial_balance: '0',
  opening_date: '',
  executive_name: '',
  executive_email: '',
  executive_phone: '',
  notes: '',
});

const optionsFrom = (rows, field) =>
  Array.from(new Set(rows.map((row) => String(row[field] || '').trim()).filter(Boolean)))
    .sort((a, b) => a.localeCompare(b, 'es'))
    .map((value) => ({ value, label: value }));

/**
 * Cuentas bancarias: datos del banco, titular, ejecutivo y saldos (calculado y según cartola).
 */
const TreasuryAccountsPanel = ({ ui, isDarkMode = false, accounts = [], loading = false, error = '', onChanged }) => {
  const styles = buildFormStyles(ui);
  const [showForm, setShowForm] = useState(false);
  const [editingRow, setEditingRow] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [formError, setFormError] = useState('');
  const [isSaving, setIsSaving] = useState(false);

  const columns = useMemo(
    () => [
      {
        key: 'account',
        label: 'Cuenta',
        sortValue: (row) => String(row.account_name || '').toLowerCase(),
        render: (row) => (
          <Stacked top={row.account_name} bottom={row.account_code} topColor={ui.text} muted={ui.muted} />
        ),
      },
      {
        key: 'bank',
        label: 'Banco',
        sortValue: (row) => String(row.bank_name || '').toLowerCase(),
        render: (row) => (
          <Stacked top={row.bank_name || '—'} bottom={row.bank_account_type} topColor={ui.text} muted={ui.muted} />
        ),
      },
      {
        key: 'number',
        label: 'N° de cuenta',
        sortValue: (row) => String(row.account_number || ''),
        render: (row) => (
          <Stacked top={row.account_number || '—'} bottom={row.currency} topColor={ui.text} muted={ui.muted} />
        ),
      },
      {
        key: 'holder',
        label: 'Titular',
        sortValue: (row) => String(row.holder_name || '').toLowerCase(),
        render: (row) => (
          <Stacked top={row.holder_name || '—'} bottom={row.holder_rut} topColor={ui.text} muted={ui.muted} />
        ),
      },
      {
        key: 'executive',
        label: 'Ejecutivo',
        sortValue: (row) => String(row.executive_name || '').toLowerCase(),
        render: (row) => (
          <Stacked
            top={row.executive_name || '—'}
            bottom={[row.executive_email, row.executive_phone].filter(Boolean).join(' · ')}
            topColor={ui.text}
            muted={ui.muted}
          />
        ),
      },
      {
        key: 'initial',
        label: 'Saldo inicial',
        align: 'right',
        defaultDir: 'desc',
        sortValue: (row) => Number(row.initial_balance || 0),
        render: (row) => (
          <Stacked
            top={money(row.initial_balance, row.currency)}
            bottom={row.opening_date ? `al ${formatDate(row.opening_date)}` : ''}
            topColor={ui.text}
            muted={ui.muted}
          />
        ),
      },
      {
        key: 'current',
        label: 'Saldo calculado',
        align: 'right',
        defaultDir: 'desc',
        sortValue: (row) => Number(row.current_balance || 0),
        render: (row) => (
          <Stacked
            top={money(row.current_balance, row.currency)}
            bottom={`+${money(row.total_income, row.currency)} / −${money(row.total_expense, row.currency)}`}
            topColor={ui.text}
            muted={ui.muted}
          />
        ),
      },
      {
        key: 'statement',
        label: 'Saldo cartola',
        align: 'right',
        defaultDir: 'desc',
        sortValue: (row) => (row.statement_balance === null ? -Infinity : Number(row.statement_balance)),
        render: (row) => {
          if (row.statement_balance === null || row.statement_balance === undefined) {
            return <span style={{ color: ui.muted }}>—</span>;
          }
          const diff = Number(row.statement_balance) - Number(row.current_balance || 0);
          return (
            <Stacked
              top={money(row.statement_balance, row.currency)}
              bottom={
                Math.abs(diff) >= 1
                  ? `Dif. ${money(diff, row.currency)} · ${formatDate(row.statement_balance_date)}`
                  : `al ${formatDate(row.statement_balance_date)}`
              }
              topColor={ui.text}
              bottomColor={Math.abs(diff) >= 1 ? '#b45309' : undefined}
              muted={ui.muted}
            />
          );
        },
      },
      {
        key: 'movements',
        label: 'Movimientos',
        align: 'center',
        defaultDir: 'desc',
        sortValue: (row) => Number(row.movement_count || 0),
        render: (row) => (
          <div style={{ display: 'grid', gap: 4, justifyItems: 'center' }}>
            <span style={{ fontWeight: 700 }}>{Number(row.movement_count || 0).toLocaleString('es-CL')}</span>
            {Number(row.pending_count || 0) > 0 ? (
              <Pill label={`${row.pending_count} pendientes`} tone="warning" isDarkMode={isDarkMode} />
            ) : row.movement_count ? (
              <Pill label="Conciliada" tone="success" isDarkMode={isDarkMode} />
            ) : null}
          </div>
        ),
      },
    ],
    [ui.text, ui.muted, isDarkMode]
  );

  const filters = useMemo(
    () => [
      {
        key: 'bank',
        label: 'Banco',
        minWidth: 160,
        options: optionsFrom(accounts, 'bank_name'),
        predicate: (row, value) => row.bank_name === value,
      },
      {
        key: 'type',
        label: 'Tipo de cuenta',
        minWidth: 160,
        options: optionsFrom(accounts, 'bank_account_type'),
        predicate: (row, value) => row.bank_account_type === value,
      },
      {
        key: 'currency',
        label: 'Moneda',
        minWidth: 110,
        options: optionsFrom(accounts, 'currency'),
        predicate: (row, value) => row.currency === value,
      },
    ],
    [accounts]
  );

  const searchText = (row) =>
    [
      row.account_code,
      row.account_name,
      row.bank_name,
      row.bank_account_type,
      row.account_number,
      row.holder_name,
      row.holder_rut,
      row.executive_name,
      row.executive_email,
      row.notes,
    ].join(' ');

  const openCreate = () => {
    setEditingRow(null);
    setForm(emptyForm());
    setFormError('');
    setShowForm(true);
  };

  const openEdit = (row) => {
    setEditingRow(row);
    setForm({
      account_name: row.account_name || '',
      bank_name: row.bank_name || '',
      bank_account_type: row.bank_account_type || '',
      account_number: row.account_number || '',
      currency: row.currency || 'CLP',
      holder_name: row.holder_name || '',
      holder_rut: row.holder_rut || '',
      initial_balance: String(row.initial_balance ?? '0'),
      opening_date: String(row.opening_date || '').slice(0, 10),
      executive_name: row.executive_name || '',
      executive_email: row.executive_email || '',
      executive_phone: row.executive_phone || '',
      notes: row.notes || '',
    });
    setFormError('');
    setShowForm(true);
  };

  const setField = (key) => (event) => setForm((prev) => ({ ...prev, [key]: event.target.value }));

  const handleSave = async () => {
    if (!form.account_name.trim()) return setFormError('El nombre de la cuenta es obligatorio.');
    const initialBalance = Number(form.initial_balance || 0);
    if (!Number.isFinite(initialBalance)) return setFormError('El saldo inicial no es válido.');
    const payload = { ...form, initial_balance: initialBalance, opening_date: form.opening_date || null };
    try {
      setIsSaving(true);
      setFormError('');
      if (editingRow) await updateTreasuryAccount(editingRow.id, payload);
      else await createTreasuryAccount(payload);
      setShowForm(false);
      onChanged?.();
    } catch (err) {
      setFormError(err.message || 'No se pudo guardar la cuenta.');
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async (row) => {
    try {
      await deleteTreasuryAccount(row.id);
      onChanged?.();
    } catch (err) {
      window.alert(err.message || 'No se pudo eliminar la cuenta.');
    }
  };

  const handleExport = (rows) => {
    downloadCsv(
      `cuentas_bancarias_${new Date().toISOString().slice(0, 10)}.csv`,
      [
        'Código',
        'Cuenta',
        'Banco',
        'Tipo de cuenta',
        'N° de cuenta',
        'Moneda',
        'Titular',
        'RUT titular',
        'Saldo inicial',
        'Fecha saldo inicial',
        'Saldo calculado',
        'Saldo cartola',
        'Fecha saldo cartola',
        'Movimientos',
        'Pendientes',
        'Ejecutivo',
        'Email ejecutivo',
        'Teléfono ejecutivo',
        'Notas',
      ],
      rows.map((row) => [
        row.account_code,
        row.account_name,
        row.bank_name,
        row.bank_account_type,
        row.account_number,
        row.currency,
        row.holder_name,
        row.holder_rut,
        Number(row.initial_balance || 0),
        formatDate(row.opening_date),
        Number(row.current_balance || 0),
        row.statement_balance ?? '',
        formatDate(row.statement_balance_date),
        row.movement_count,
        row.pending_count,
        row.executive_name,
        row.executive_email,
        row.executive_phone,
        row.notes,
      ])
    );
  };

  const clpTotal = accounts
    .filter((row) => (row.currency || 'CLP') === 'CLP')
    .reduce((acc, row) => acc + Number(row.current_balance || 0), 0);
  const subtitle = `${accounts.length} cuenta${accounts.length === 1 ? '' : 's'} · Saldo calculado CLP ${money(clpTotal)}`;
  const bankOptions = form.bank_name && !BANK_OPTIONS.includes(form.bank_name)
    ? [form.bank_name, ...BANK_OPTIONS]
    : BANK_OPTIONS;
  const typeOptions = form.bank_account_type && !ACCOUNT_TYPE_OPTIONS.includes(form.bank_account_type)
    ? [form.bank_account_type, ...ACCOUNT_TYPE_OPTIONS]
    : ACCOUNT_TYPE_OPTIONS;

  return (
    <div style={{ display: 'grid', gap: 12 }}>
      <TreasuryTable3
        rows={accounts}
        loading={loading}
        error={error}
        isDarkMode={isDarkMode}
        title="Cuentas bancarias"
        subtitle={subtitle}
        columns={columns}
        defaultSort={{ key: 'account', dir: 'asc' }}
        searchPlaceholder="Nombre, banco, N° de cuenta, titular…"
        searchText={searchText}
        filters={filters}
        headerActions={[
          { key: 'export', label: 'Exportar', icon: Download, onClick: handleExport, disabled: !accounts.length },
          { key: 'add', label: 'Nueva cuenta', icon: Plus, variant: 'primary', onClick: openCreate },
        ]}
        onEdit={openEdit}
        onDelete={handleDelete}
        deleteConfirmText={(row) => `¿Eliminar la cuenta ${row.account_name}?`}
        emptyText="Aún no hay cuentas bancarias. Crea la primera con “Nueva cuenta”."
        minTableWidth={1180}
      />

      {showForm ? (
        <TreasuryDrawer
          ui={ui}
          title={editingRow ? `Editar cuenta ${editingRow.account_code}` : 'Nueva cuenta bancaria'}
          subtitle="Datos de la cuenta, titular y ejecutivo del banco."
          error={formError}
          saving={isSaving}
          onClose={() => setShowForm(false)}
          onSave={handleSave}
        >
          <label style={styles.label}>
            Nombre de la cuenta
            <input
              value={form.account_name}
              onChange={setField('account_name')}
              placeholder="Ej: Cuenta corriente operacional"
              style={styles.input}
            />
          </label>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
            <label style={styles.label}>
              Banco
              <select value={form.bank_name} onChange={setField('bank_name')} style={styles.input}>
                {bankOptions.map((bank) => (
                  <option key={bank} value={bank}>
                    {bank}
                  </option>
                ))}
              </select>
            </label>
            <label style={styles.label}>
              Tipo de cuenta
              <select value={form.bank_account_type} onChange={setField('bank_account_type')} style={styles.input}>
                {typeOptions.map((type) => (
                  <option key={type} value={type}>
                    {type}
                  </option>
                ))}
              </select>
            </label>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: 10 }}>
            <label style={styles.label}>
              N° de cuenta
              <input value={form.account_number} onChange={setField('account_number')} style={styles.input} />
            </label>
            <label style={styles.label}>
              Moneda
              <select value={form.currency} onChange={setField('currency')} style={styles.input}>
                {CURRENCY_OPTIONS.map((currency) => (
                  <option key={currency} value={currency}>
                    {currency}
                  </option>
                ))}
              </select>
            </label>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: 10 }}>
            <label style={styles.label}>
              Titular
              <input value={form.holder_name} onChange={setField('holder_name')} style={styles.input} />
            </label>
            <label style={styles.label}>
              RUT titular
              <input
                value={form.holder_rut}
                onChange={setField('holder_rut')}
                placeholder="76.123.456-7"
                style={styles.input}
              />
            </label>
          </div>
          <p style={styles.sectionTitle}>Saldo inicial</p>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
            <label style={styles.label}>
              Monto
              <input
                type="number"
                value={form.initial_balance}
                onChange={setField('initial_balance')}
                style={styles.input}
              />
            </label>
            <label style={styles.label}>
              Al día
              <input type="date" value={form.opening_date} onChange={setField('opening_date')} style={styles.input} />
            </label>
          </div>
          <p style={{ margin: 0, fontSize: 11, color: ui.muted, lineHeight: 1.4 }}>
            Saldo de la cuenta antes del primer movimiento registrado. El saldo calculado suma los ingresos y resta
            los egresos a partir de este monto.
          </p>
          <p style={styles.sectionTitle}>Ejecutivo del banco</p>
          <label style={styles.label}>
            Nombre
            <input value={form.executive_name} onChange={setField('executive_name')} style={styles.input} />
          </label>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
            <label style={styles.label}>
              Email
              <input
                type="email"
                value={form.executive_email}
                onChange={setField('executive_email')}
                style={styles.input}
              />
            </label>
            <label style={styles.label}>
              Teléfono
              <input value={form.executive_phone} onChange={setField('executive_phone')} style={styles.input} />
            </label>
          </div>
          <label style={styles.label}>
            Notas
            <textarea
              value={form.notes}
              onChange={setField('notes')}
              rows={3}
              style={{ ...styles.input, height: 'auto', padding: '8px 10px', resize: 'vertical' }}
            />
          </label>
        </TreasuryDrawer>
      ) : null}
    </div>
  );
};

export default TreasuryAccountsPanel;
